import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '../../../../components/ui';
import { BRAND, getThemeColors } from '../../../../lib/theme';
import { v1Absences, absenceGrundLabel, absenceOverlapPeriod, AbsenceApiError, type V1Absence } from '../../../../lib/v1';
import { fmtDate, Th } from './BetreuungenTabHelpers';
import { DateTimeLocalInput } from '../../../../components/DateTimeInput';
import { DateInput } from '../../../../components/DateInput';
import { Icon } from '../../../../components/icons';
import { BK_ABSENCE_GRUND } from '../../../../lib/bkAvailability';

/**
 * 'pb' = Abwesenheiten eines Pflegebedürftigen, 'bk' = Abwesenheiten einer
 * Betreuungskraft. Gleiche Tabelle und Endpunkte. Bei der BK gibt es seit
 * 23.10.2026 keine Art mehr (nur „Abwesenheit": Von = Pflicht, Bis = optional;
 * ohne Bis ist die Abwesenheit offen, wie es die API erlaubt); `absences.grund` bekommt beim Anlegen den festen Wert
 * BK_ABSENCE_GRUND, Bestandsdaten behalten ihren Grund.
 */
export type AbsenceVariant = 'pb' | 'bk';

/** Wählbare Gründe einer PB-Abwesenheit: nur Krankenhaus (KH) und Vertragspause (VP). */
export const PB_ABSENCE_GRUENDE: { value: string; labelKey: string }[] = [
  { value: 'Krankenhaus', labelKey: 'pflege.absences.grund_options.krankenhaus' },
  { value: 'Vertragspause', labelKey: 'pflege.absences.grund_options.vertragspause' },
];

/** localStorage-Key für den Auf-/Zuklapp-Zustand der Abwesenheiten-Karte. */
export const ABSENCES_COLLAPSED_KEY = 'sh24.absences.collapsed';

export function AbsenceCard({
  absences, dark, onAdd, onEdit, onDeleted, variant = 'pb',
}: {
  absences: V1Absence[];
  dark: boolean;
  onAdd: () => void;
  onEdit: (a: V1Absence) => void;
  onDeleted: () => void;
  variant?: AbsenceVariant;
}) {
  const tc = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const bk = variant === 'bk';
  const titleLabel = tr(bk ? 'betreuung.availability.title' : 'pflege.absences.title');
  const addLabel = tr(bk ? 'betreuung.availability.add' : 'pflege.absences.add');
  const emptyLabel = tr(bk ? 'betreuung.availability.empty' : 'pflege.absences.empty');
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  // Auf-/Zuklappen der Karte; persistiert in localStorage (Default: ausgeklappt).
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(ABSENCES_COLLAPSED_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(ABSENCES_COLLAPSED_KEY, collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);
  // Kommt eine neue Abwesenheit hinzu → Karte aufklappen, damit sie sichtbar ist.
  const prevCount = useRef(absences.length);
  useEffect(() => {
    if (absences.length > prevCount.current) setCollapsed(false);
    prevCount.current = absences.length;
  }, [absences.length]);
  const toggle = () => setCollapsed(c => !c);

  const handleDelete = async (id: number) => {
    setBusy(true);
    try { await v1Absences.delete(id); onDeleted(); } catch {/* ignore */}
    finally { setBusy(false); setDeleteId(null); }
  };

  return (
    <Card dark={dark} style={{ padding: 0, overflow: 'hidden' }}>
      <div
        onClick={toggle}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '14px 16px', borderBottom: collapsed ? 'none' : `1px solid ${tc.border}`, cursor: 'pointer' }}
      >
        <button
          type="button"
          onClick={e => { e.stopPropagation(); toggle(); }}
          aria-expanded={!collapsed}
          aria-controls="absences-card-body"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: tc.heading, font: 'inherit' }}
        >
          <Icon.Chevron dir={collapsed ? 'right' : 'down'} c={tc.muted} />
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: tc.heading }}>
            {titleLabel} <span style={{ color: tc.muted, fontWeight: 500 }}>({absences.length})</span>
          </h3>
        </button>
        <span onClick={e => e.stopPropagation()}>
          <Button dark={dark} variant="secondary" size="sm" onClick={() => { setCollapsed(false); onAdd(); }}>+ {addLabel}</Button>
        </span>
      </div>
      {collapsed ? null : absences.length === 0 ? (
        <div id="absences-card-body" style={{ padding: 24, textAlign: 'center', color: tc.muted, fontSize: 13 }}>{emptyLabel}</div>
      ) : (
        <div id="absences-card-body" style={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: tc.tableHeader }}>
              <tr>
                {!bk && <Th dark={dark}>{tr('pflege.absences.th.grund')}</Th>}
                <Th dark={dark}>{tr('pflege.absences.th.from')}</Th>
                <Th dark={dark}>{tr('pflege.absences.th.to')}</Th>
                <Th dark={dark}> </Th>
              </tr>
            </thead>
            <tbody>
              {absences.map(a => (
                <tr key={a.id} style={{ borderTop: `1px solid ${tc.borderSoft}` }}>
                  {!bk && <td style={{ padding: '12px 16px' }}><strong style={{ color: tc.heading }}>{absenceGrundLabel(a.grund, tr)}</strong></td>}
                  <td style={{ padding: '12px 16px' }}>{fmtDate(a.startdatum)}</td>
                  <td style={{ padding: '12px 16px' }}>{a.enddatum ? fmtDate(a.enddatum) : <span style={{ color: tc.muted }}>{tr('pflege.absences.open')}</span>}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {deleteId === a.id ? (
                      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        <button disabled={busy} onClick={() => handleDelete(a.id)} style={{ fontSize: 12, fontWeight: 600, background: '#b42318', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>{tr('common.action.delete')}</button>
                        <button onClick={() => setDeleteId(null)} style={{ fontSize: 12, background: 'none', border: `1px solid ${tc.border}`, borderRadius: 6, padding: '4px 8px', color: tc.muted, cursor: 'pointer' }}>{tr('common.action.cancel')}</button>
                      </span>
                    ) : (
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        <button onClick={() => onEdit(a)} title={tr('common.action.edit')} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${tc.border}`, background: 'transparent', color: tc.muted, cursor: 'pointer', fontSize: 12 }}>✎</button>
                        <button onClick={() => setDeleteId(a.id)} title={tr('common.action.delete')} style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid #fda29b', background: '#fff5f5', color: '#b42318', cursor: 'pointer', fontSize: 12 }}>✕</button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export function AbsenceForm({
  dark, userId, initial, onSaved, onCancel, variant = 'pb',
}: {
  dark: boolean;
  userId: number;
  initial: V1Absence | null;
  onSaved: () => void;
  onCancel: () => void;
  variant?: AbsenceVariant;
}) {
  const tc = getThemeColors(dark);
  const { t: tr } = useTranslation();
  // Startdatum mit Uhrzeit → datetime-local Wert (YYYY-MM-DDTHH:mm).
  const toLocalInput = (v?: string | null): string => {
    if (!v) return '';
    // akzeptiert "YYYY-MM-DD HH:MM:SS", "YYYY-MM-DDTHH:MM[:SS]" oder reines Datum
    const s = String(v).replace(' ', 'T');
    if (s.length >= 16) return s.slice(0, 16);
    if (s.length === 10) return `${s}T00:00`; // reines Datum → Mitternacht
    return s;
  };
  const [grund, setGrund] = useState(initial?.grund ?? '');
  // Gespeicherter Grund außerhalb der wählbaren Liste (Altbestand, nur PB-Formular).
  const initialGrund = (initial?.grund ?? '').trim();
  const selectable = PB_ABSENCE_GRUENDE.map(g => g.value);
  const legacyGrund = initialGrund !== '' && !selectable.includes(initialGrund) ? initialGrund : null;
  // BK: keine Art-Auswahl. Neu → fester Grund, Bearbeiten → Bestandsgrund unverändert.
  const effectiveGrund = variant === 'bk' ? (initialGrund || BK_ABSENCE_GRUND) : grund.trim();
  // BK: Bis ist immer sichtbar und optional (leer = offenes Ende), die Checkbox „Enddatum setzen" entfällt.
  const endAlwaysShown = variant === 'bk';
  const [start, setStart] = useState(toLocalInput(initial?.startdatum));
  const [hasEndChecked, setHasEnd] = useState<boolean>(!!initial?.enddatum);
  const hasEnd = endAlwaysShown || hasEndChecked;
  const [end, setEnd] = useState(initial?.enddatum?.slice(0, 10) ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputStyle: React.CSSProperties = {
    fontSize: 13, padding: '9px 11px', border: `1px solid ${tc.border}`, borderRadius: 8,
    background: tc.inputBg, color: tc.text, fontFamily: 'Outfit,sans-serif',
    width: '100%', boxSizing: 'border-box', outline: 'none',
  };
  const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: tc.muted, marginBottom: 6, display: 'block' };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (variant !== 'bk' && !grund.trim()) { setError(tr('pflege.absences.err.grund')); return; }
    if (!start) { setError(tr('pflege.absences.err.start')); return; }
    if (hasEnd && !end && !endAlwaysShown) { setError(tr('pflege.absences.err.end')); return; }
    // Enddatum (Datum) vs. Startdatum (Datum-Anteil) vergleichen.
    if (hasEnd && end && end < start.slice(0, 10)) { setError(tr('pflege.absences.err.range')); return; }
    setSaving(true); setError(null);
    // Backend erwartet ein date-parsebares Startdatum mit Uhrzeit.
    const startPayload = start.replace('T', ' ') + ':00';
    const endPayload = hasEnd && end ? end : null;
    try {
      if (initial?.id) {
        await v1Absences.update(initial.id, { grund: effectiveGrund, startdatum: startPayload, enddatum: endPayload });
      } else {
        await v1Absences.create({ user_id: userId, grund: effectiveGrund, startdatum: startPayload, enddatum: endPayload });
      }
      onSaved();
    } catch (err) {
      if (variant === 'bk' && err instanceof AbsenceApiError && err.code === 'E_ABSENCE_OVERLAP') {
        // Die API-Meldung nennt den Grund der bestehenden Abwesenheit; BK haben keine Art mehr.
        const per = absenceOverlapPeriod(err.message);
        setError(per
          ? tr('betreuung.availability.err.overlap', { von: per.von, bis: per.bis === 'offen' ? tr('pflege.absences.open') : per.bis })
          : tr('betreuung.availability.err.overlap_plain'));
      } else {
        setError(err instanceof Error ? err.message : tr('pflege.absences.err.save'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {variant !== 'bk' && (
      <div>
        <label style={labelStyle}>{tr('pflege.absences.th.grund')}</label>
        <select value={grund} onChange={e => setGrund(e.target.value)} style={inputStyle}>
          <option value="">{tr('pflege.absences.grund_ph')}</option>
          {/* PB-Abwesenheiten: nur Krankenhaus (KH) und Vertragspause (VP).
              Ein Altbestand-Grund (z. B. „Urlaub") bleibt beim Bearbeiten
              als eigene Option erhalten, damit er nicht stillschweigend
              umgeschrieben wird. */}
          {PB_ABSENCE_GRUENDE.map(g => (
            <option key={g.value} value={g.value}>{tr(g.labelKey)}</option>
          ))}
          {legacyGrund && <option value={legacyGrund}>{absenceGrundLabel(legacyGrund, tr)}</option>}
        </select>
      </div>
      )}
      <div>
        <label style={labelStyle}>{tr('pflege.absences.th.from')}{variant === 'bk' ? ' *' : ''}</label>
        {variant === 'bk' ? (
          // BK: nur Datum (keine Uhrzeit), wie das Enddatum.
          <DateInput ariaLabel={tr('pflege.absences.th.from')} value={start.slice(0, 10)} onChange={v => setStart(v ? `${v}T00:00` : '')} style={inputStyle} />
        ) : (
          <DateTimeLocalInput ariaLabel={tr('pflege.absences.th.from')} value={start} onChange={setStart} style={inputStyle} />
        )}
      </div>
      <div>
        {endAlwaysShown ? (
          <label style={labelStyle}>{tr('pflege.absences.th.to')} <span style={{ fontWeight: 400 }}>{tr('betreuung.availability.end_optional')}</span></label>
        ) : (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: tc.text, cursor: 'pointer', marginBottom: hasEnd ? 8 : 0 }}>
          <input type="checkbox" checked={hasEnd} onChange={e => { setHasEnd(e.target.checked); if (!e.target.checked) setEnd(''); }} style={{ cursor: 'pointer' }} />
          {tr('pflege.absences.set_end')}
        </label>
        )}
        {hasEnd && (
          <DateInput ariaLabel={tr('pflege.absences.th.to')} value={end} min={start.slice(0, 10) || undefined} onChange={setEnd} style={inputStyle} />
        )}
      </div>
      {error && <div style={{ fontSize: 12, color: '#b42318' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
        <button type="button" onClick={onCancel} style={{ fontSize: 13, padding: '10px 18px', background: 'none', border: `1px solid ${tc.border}`, borderRadius: 8, color: tc.muted, cursor: 'pointer' }}>
          {tr('common.action.cancel')}
        </button>
        <button type="submit" disabled={saving} style={{ fontSize: 13, fontWeight: 600, padding: '10px 18px', background: BRAND, color: '#fff', border: 'none', borderRadius: 8, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? '…' : tr('common.action.save')}
        </button>
      </div>
    </form>
  );
}
